import axios from 'axios';
import { setAlert } from './alert';
import {
    GET_CAN_RETURNS,
    CAN_RETURN_LOADING,
    CAN_RETURN_ERROR,
    UPDATE_CAN_RETURN,
    GET_DELIVERY_AGENTS
} from './types';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const FILTER_KEYS = ['user', 'status', 'search', 'transactionId', 'phone', 'agentName', 'serviceableArea', 'startDate', 'endDate'];

const buildFilterParams = (filters = {}) => {
    const params = {};
    FILTER_KEYS.forEach(key => {
        if (filters[key]) params[key] = filters[key];
    });
    if (filters.latest !== undefined) params.latest = filters.latest;
    return params;
};

/**
 * Get a page of can returns with server-side filtering and search
 * @param {Object} filters
 * @param {string} [filters.user] - Filter to this user's can returns only
 * @param {string} [filters.status] - Status tab to load
 * @param {string} [filters.search] - Customer name/phone or transaction ID
 * @param {string} [filters.transactionId] - Transaction ID (partial match)
 * @param {string} [filters.phone] - Customer phone (partial match)
 * @param {string} [filters.agentName] - Pickup agent name (partial match)
 * @param {string} [filters.serviceableArea] - Serviceable area ID
 * @param {string} [filters.startDate] - Pickup date from (YYYY-MM-DD)
 * @param {string} [filters.endDate] - Pickup date to (YYYY-MM-DD)
 * @param {boolean} [filters.latest] - Newest first when true
 * @param {number} [filters.page] - Page number
 * @param {number} [filters.limit] - Page size
 */
export const getCanReturns = ({ page = 1, limit = 20, ...filters } = {}) => async dispatch => {
    try {
        dispatch({ type: CAN_RETURN_LOADING });

        const params = { page, limit, ...buildFilterParams(filters) };

        const res = await axios.get(`${API_URL}/can-returns`, { params });

        if (res.data.success) {
            dispatch({
                type: GET_CAN_RETURNS,
                payload: {
                    canReturns: res.data.data,
                    total: res.data.total,
                    pages: res.data.pagination.pages,
                    counts: res.data.counts
                }
            });
        } else {
            dispatch({
                type: CAN_RETURN_ERROR,
                payload: res.data.message || 'Failed to fetch can returns'
            });
            dispatch(setAlert(res.data.message || 'Failed to fetch can returns', 'danger'));
        }
    } catch (err) {
        console.error('Error fetching can returns:', err);
        dispatch({
            type: CAN_RETURN_ERROR,
            payload: err.response?.data?.message || 'Server error'
        });
        dispatch(setAlert('Failed to fetch can returns', 'danger'));
    }
};

/**
 * Get agents available to assign to a pending can return pickup
 * @param {string} id - The ID of the can return
 */
export const getAvailableAgentsForReturn = (id) => async dispatch => {
    try {
        const res = await axios.get(`${API_URL}/can-returns/available-agents?id=${id}`);

        if (res.data.success) {
            dispatch({
                type: GET_DELIVERY_AGENTS,
                payload: res.data.agents
            });
        } else {
            dispatch(setAlert(res.data.message || 'Failed to fetch available agents', 'danger'));
        }
    } catch (err) {
        console.error('Error fetching available agents for can return:', err);
        dispatch(setAlert('Failed to fetch available agents', 'danger'));
    }
};

/**
 * Assign an agent to a can return pickup
 * @param {string} canReturnId - The ID of the can return to assign
 * @param {string} agentId - The ID of the agent to assign
 */
export const assignCanReturnAgent = (canReturnId, agentId) => async dispatch => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = JSON.stringify({ canReturnId, agentId });

        const res = await axios.post(`${API_URL}/can-returns/assign`, body, config);

        if (res.data.success) {
            dispatch({
                type: UPDATE_CAN_RETURN,
                payload: res.data.data
            });
            dispatch(setAlert('Agent assigned successfully', 'success'));
        } else {
            dispatch(setAlert(res.data.message || 'Failed to assign agent', 'danger'));
        }
    } catch (err) {
        console.error('Error assigning can return agent:', err);
        dispatch(setAlert('Failed to assign agent', 'danger'));
    }
};

/**
 * Update the status of a can return
 * @param {string} id - Can return ID
 * @param {string} status - New status
 * @param {string} comment - Optional comment
 */
export const updateCanReturnStatus = (id, status, comment = '') => async dispatch => {
    try {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const body = JSON.stringify({ status, comment });

        const res = await axios.put(`${API_URL}/can-returns/${id}/status`, body, config);

        if (res.data.success) {
            dispatch({
                type: UPDATE_CAN_RETURN,
                payload: res.data.data
            });
            dispatch(setAlert('Can return status updated', 'success'));
        } else {
            dispatch(setAlert(res.data.message || 'Failed to update status', 'danger'));
        }
    } catch (err) {
        console.error('Error updating can return status:', err);
        dispatch(setAlert('Failed to update status', 'danger'));
    }
};

const convertCanReturnsToCSV = (canReturns) => {
    if (!canReturns || canReturns.length === 0) return '';
    const headers = [
        'Transaction ID', 'Status', 'Customer Name', 'Customer Phone', 'Address',
        'Serviceable Area', 'Cans', 'Agent Name', 'Pickup Date', 'Assigned Time',
        'Completed Time', 'Requested On', 'Comment'
    ];

    const escapeCSV = (val) => {
        if (val === null || val === undefined) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
    };
    const formatDate = (val) => escapeCSV(val ? new Date(val).toLocaleString() : '');

    const rows = canReturns.map(c => [
        escapeCSV(c.transactionId),
        escapeCSV(c.status),
        escapeCSV(c.userName),
        escapeCSV(c.userPhone),
        escapeCSV(c.address),
        escapeCSV(c.serviceableAreaName),
        escapeCSV(c.numberOfCans),
        escapeCSV(c.agentName),
        formatDate(c.scheduledFor),
        formatDate(c.assignedTime),
        formatDate(c.completedTime),
        formatDate(c.createdAt),
        escapeCSV(c.comment)
    ]);

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
};

/**
 * Export can returns as a filtered, unpaginated CSV download
 * @param {Object} filters - Same filters as getCanReturns (page and limit are ignored)
 */
export const exportCanReturns = (filters = {}) => async dispatch => {
    try {
        const res = await axios.get(`${API_URL}/can-returns/export`, { params: buildFilterParams(filters) });

        if (!res.data.success) {
            dispatch(setAlert(res.data.message || 'Failed to export can returns', 'danger'));
            return;
        }

        const csvContent = convertCanReturnsToCSV(res.data.data.canReturns);
        if (!csvContent) {
            dispatch(setAlert('No can returns found to export', 'warning', 3000));
            return;
        }

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `can-returns-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        dispatch(setAlert('Can returns exported successfully', 'success', 3000));
    } catch (err) {
        console.error('Error exporting can returns:', err);
        dispatch(setAlert(err.response?.data?.message || 'Failed to export can returns', 'danger'));
    }
};
